// ============================================
// PROPERTY WIZARD - Extrait de App.js
// Extraction: Étape 1 - Découpage monolithe
// Behavior: INCHANGÉ
// ============================================
import React, { useState, useEffect } from "react";
import axios from "axios";

const PropertyWizard = ({ onClose, onPropertyCreated }) => {
  console.log("🎨 Rendu PropertyWizard autonome");
  
  // States autonomes du wizard
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    transaction_type: '',
    category: '',
    subcategory: '',
    general_info: {},
    location: {},
    proprietaire: {},
    negociateur: '',
    descriptions: {}
  });
  const [wizardTypes, setWizardTypes] = useState({
    transaction_types: [],
    categories: [],
    subcategories: {}
  });
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [agencyNegotiators, setAgencyNegotiators] = useState([]); // 🆕 Liste des négociateurs
  
  // Autocomplete states
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  const [ownerAddressSuggestions, setOwnerAddressSuggestions] = useState([]);
  const [showOwnerAddressSuggestions, setShowOwnerAddressSuggestions] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingOwnerAddresses, setLoadingOwnerAddresses] = useState(false);

  // REMOVED: Detection de l'URL /shared/:token maintenant gérée par React Router

  // 🆕 Charger les négociateurs de l'agence au montage
  useEffect(() => {
    const loadNegotiators = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/negotiators`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('🔍 Négociateurs reçus de l\'API:', response.data);
        
        setAgencyNegotiators(response.data);
      } catch (error) {
        console.error('Erreur chargement négociateurs:', error);
      }
    };
    
    loadNegotiators();
  }, []);

  // Charger les types au montage
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wizard/types`);
        setWizardTypes(response.data);
      } catch (error) {
        console.error("Erreur chargement types:", error);
      }
    };
    loadTypes();
  }, []);
  
  // Debounced address search - Using International Photon API (OpenStreetMap)
  useEffect(() => {
    const searchAddresses = async () => {
      const query = wizardData.location?.adresse_complete || '';
      console.log("🔍 Address search triggered, query:", query);
      if (query.length < 3) {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
        return;
      }
      
      setLoadingAddresses(true);
      console.log("📡 Fetching addresses from Photon (international)...");
      try {
        // Photon API - OpenStreetMap based, international, free
        const response = await fetch(
          `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=8`
        );
        const data = await response.json();
        
        // Transform Photon format to our format
        const suggestions = (data.features || []).map(feature => {
          const props = feature.properties;
          const street = props.street || props.name || '';
          const housenumber = props.housenumber || '';
          const city = props.city || props.town || props.village || '';
          const postcode = props.postcode || '';
          const country = props.country || '';
          
          const addressParts = [];
          if (housenumber) addressParts.push(housenumber);
          if (street) addressParts.push(street);
          
          return {
            properties: {
              label: `${addressParts.join(' ')}, ${postcode} ${city}${country ? ', ' + country : ''}`.trim(),
              street: street,
              housenumber: housenumber,
              city: city,
              postcode: postcode,
              country: country
            }
          };
        });

        console.log("✅ Addresses received:", suggestions.length);
        setAddressSuggestions(suggestions);
        setShowAddressSuggestions(true);
      } catch (error) {
        console.error("❌ Erreur recherche adresses:", error);
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      } finally {
        setLoadingAddresses(false);
      }
    };
    
    const debounceTimer = setTimeout(searchAddresses, 300);
    return () => clearTimeout(debounceTimer);
  }, [wizardData.location?.adresse_complete]);
  
  // Debounced contact search - Using existing backend endpoint
  useEffect(() => {
    const searchContacts = async () => {
      const query = wizardData.proprietaire?.search_query || '';
      console.log("🔍 Contact search triggered, query:", query);
      if (query.length < 2) {
        setContactSuggestions([]);
        setShowContactSuggestions(false);
        return;
      }
      
      setLoadingContacts(true);
      console.log("📡 Fetching contacts from backend...");
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/contacts/search/duplicates?nom=${encodeURIComponent(query)}&prenom=${encodeURIComponent(query)}&societe=${encodeURIComponent(query)}`
        );
        console.log("✅ Contacts received:", response.data.length);
        setContactSuggestions(response.data || []);
        setShowContactSuggestions(true);
      } catch (error) {
        console.error("❌ Erreur recherche contacts:", error);
        setContactSuggestions([]);
        setShowContactSuggestions(false);
      } finally {
        setLoadingContacts(false);
      }
    };
    
    const debounceTimer = setTimeout(searchContacts, 300);
    return () => clearTimeout(debounceTimer);
  }, [wizardData.proprietaire?.search_query]);

  // Debounced owner address search - Using International Photon API
  useEffect(() => {
    const searchOwnerAddresses = async () => {
      const query = wizardData.proprietaire?.adresse_complete || '';
      console.log("🔍 Owner address search triggered, query:", query);
      if (query.length < 3) {
        setOwnerAddressSuggestions([]);
        setShowOwnerAddressSuggestions(false);
        return;
      }
      
      setLoadingOwnerAddresses(true);
      console.log("📡 Fetching owner addresses from Photon (international)...");
      try {
        const response = await fetch(
          `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=8`
        );
        const data = await response.json();
        
        const suggestions = (data.features || []).map(feature => {
          const props = feature.properties;
          const street = props.street || props.name || '';
          const housenumber = props.housenumber || '';
          const city = props.city || props.town || props.village || '';
          const postcode = props.postcode || '';
          const country = props.country || '';
          
          const addressParts = [];
          if (housenumber) addressParts.push(housenumber);
          if (street) addressParts.push(street);
          
          return {
            properties: {
              label: `${addressParts.join(' ')}, ${postcode} ${city}${country ? ', ' + country : ''}`.trim(),
              street: street,
              housenumber: housenumber,
              city: city,
              postcode: postcode,
              country: country
            }
          };
        });

        console.log("✅ Owner addresses received:", suggestions.length);
        setOwnerAddressSuggestions(suggestions);
        setShowOwnerAddressSuggestions(true);
      } catch (error) {
        console.error("❌ Erreur recherche adresses propriétaire:", error);
        setOwnerAddressSuggestions([]);
        setShowOwnerAddressSuggestions(false);
      } finally {
        setLoadingOwnerAddresses(false);
      }
    };
    
    const debounceTimer = setTimeout(searchOwnerAddresses, 300);
    return () => clearTimeout(debounceTimer);
  }, [wizardData.proprietaire?.adresse_complete]);

  const getStepTitle = () => {
    switch(wizardStep) {
      case 1: return "Type de transaction";
      case 2: return "Catégorie de bien";
      case 3: return "Type d'activité"; 
      case 4: return "Informations générales";
      case 5: return "Localisation";
      case 6: return "Propriétaire";
      case 7: return "Descriptifs";
      default: return "Étape " + wizardStep;
    }
  };

  const nextStep = () => setWizardStep(prev => Math.min(prev + 1, 7));
  const prevStep = () => setWizardStep(prev => Math.max(prev - 1, 1));

  const saveDraft = async () => {
    try {
      // Minimal payload - only essential fields
      const payload = {
        transaction_type: wizardData.transaction_type,
        category: wizardData.category,
        subcategory: wizardData.subcategory
      };
      
      // Add numero_mandat if present
      if (wizardData.general_info?.numero_mandat) {
        payload.numero_mandat = wizardData.general_info.numero_mandat;
      }
      
      // Add location data if present
      if (wizardData.location && (wizardData.location.adresse || wizardData.location.ville)) {
        payload.informations_confidentielles = {
          adresse: wizardData.location.adresse || wizardData.location.adresse_complete || null,
          ville: wizardData.location.ville || null,
          code_postal: wizardData.location.code_postal || null,
          pays: wizardData.location.pays || 'France'
        };
      }
      
      // Add owner data if present
      if (wizardData.proprietaire && (wizardData.proprietaire.nom || wizardData.proprietaire.prenom)) {
        if (!payload.informations_confidentielles) {
          payload.informations_confidentielles = {};
        }
        
        payload.informations_confidentielles.proprietaire = {
          contact_id: wizardData.proprietaire.contact_id || null,
          prenom: wizardData.proprietaire.prenom || null,
          nom: wizardData.proprietaire.nom || null,
          societe: wizardData.proprietaire.societe || null
        };
        
        // Add phone if present
        if (wizardData.proprietaire.telephone) {
          payload.informations_confidentielles.proprietaire.telephones = [
            { type: "mobile", numero: wizardData.proprietaire.telephone, principal: true }
          ];
        }
        
        // Add email if present
        if (wizardData.proprietaire.email) {
          payload.informations_confidentielles.proprietaire.emails = [
            { email: wizardData.proprietaire.email, type: "professionnel", principal: true }
          ];
        }
      }
      
      // Add negotiator if present
      if (wizardData.negociateur) {
        payload.negociateur = {
          negociateur: wizardData.negociateur
        };
      }
      
      // Add price if present
      if (wizardData.general_info?.prix_estime) {
        payload.honoraires_calcul = {
          prix_vente: wizardData.general_info.prix_estime
        };
      }
      
      // Add surface if present - separate from price
      if (wizardData.general_info?.superficie) {
        payload.composition = {
          superficie_totale: wizardData.general_info.superficie
        };
      }
      
      // Add dynamic fields specific to property type
      if (wizardData.general_info && Object.keys(wizardData.general_info).length > 0) {
        // Initialize bien_general if not exists
        if (!payload.bien_general) {
          payload.bien_general = {};
        }
        
        // Map category and subcategory to type_bien and activite_principale
        if (wizardData.category && wizardData.subcategory) {
          // Determine type_bien from category
          const categoryMapping = {
            "Fonds de commerce": "Fonds de commerce",
            "Licence": "Fonds de commerce", // Licences are also FDC
            "Murs commerciaux": "Murs",
            "Locaux professionnels": "Locaux commerciaux",
            "Bureaux": "Bureaux",
            "Industriel": "Industriel",
            "Droit au bail": "Droit au bail",
            "Terrain": "Terrain"
          };
          
          payload.bien_general.type_bien = categoryMapping[wizardData.category] || wizardData.category;
          payload.bien_general.activite_principale = wizardData.subcategory;
          
          console.log(`📋 Mapping wizard data: category="${wizardData.category}" → type_bien="${payload.bien_general.type_bien}", activite="${payload.bien_general.activite_principale}"`);
        }
        
        // Add secteur and zone_attractivite from location
        if (wizardData.location?.secteur) {
          payload.bien_general.secteur = wizardData.location.secteur;
        }
        if (wizardData.location?.zone_attractivite) {
          payload.bien_general.zone_attractivite = wizardData.location.zone_attractivite;
        }
        
        // Restaurant/Bar fields
        if (wizardData.general_info.nombre_couverts !== undefined) {
          payload.bien_general.nombre_couverts = wizardData.general_info.nombre_couverts;
        }
        if (wizardData.general_info.terrasse) {
          payload.bien_general.terrasse = wizardData.general_info.terrasse;
        }
        if (wizardData.general_info.cuisine_equipee) {
          payload.bien_general.cuisine_equipee = wizardData.general_info.cuisine_equipee;
        }
        
        // Licence fields
        if (wizardData.general_info.type_licence) {
          payload.bien_general.type_licence = wizardData.general_info.type_licence;
        }
        if (wizardData.general_info.numero_licence) {
          payload.bien_general.numero_licence = wizardData.general_info.numero_licence;
        }
        if (wizardData.general_info.validite_licence) {
          payload.bien_general.validite_licence = wizardData.general_info.validite_licence;
        }
        if (wizardData.general_info.transferable) {
          payload.bien_general.transferable = wizardData.general_info.transferable;
        }
        if (wizardData.general_info.conditions_transfert) {
          payload.bien_general.conditions_transfert = wizardData.general_info.conditions_transfert;
        }
        
        // Bureaux fields
        if (wizardData.general_info.postes_travail !== undefined) {
          payload.bien_general.postes_travail = wizardData.general_info.postes_travail;
        }
        if (wizardData.general_info.salles_reunion !== undefined) {
          payload.bien_general.salles_reunion = wizardData.general_info.salles_reunion;
        }
        if (wizardData.general_info.parking_inclus) {
          payload.bien_general.parking_inclus = wizardData.general_info.parking_inclus;
        }
        if (wizardData.general_info.fibre_optique) {
          payload.bien_general.fibre_optique = wizardData.general_info.fibre_optique;
        }
        if (wizardData.general_info.climatisation) {
          payload.bien_general.climatisation = wizardData.general_info.climatisation;
        }
      }
      
      console.log("💾 Payload to send:", JSON.stringify(payload, null, 2));
      
      let response;
      if (currentDraftId) {
        response = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/properties/draft/${currentDraftId}`, 
          payload
        );
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/properties/draft`, 
          payload
        );
        setCurrentDraftId(response.data.id);
      }
      
      console.log("✅ Draft saved successfully! ID:", response.data.id);
      return response.data;
      
    } catch (error) {
      console.error("❌ ERROR in saveDraft:");
      console.error("Error message:", error.message);
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", JSON.stringify(error.response.data, null, 2));
      }
      // Don't throw - return error info instead
      return { error: true, message: error.message, response: error.response };
    }
  };

  const finishWizard = async () => {
    console.log("🎯 Starting finishWizard...");
    
    // Step 1: Create contact if new proprietaire (not from existing)
    if (wizardData.proprietaire && !wizardData.proprietaire.from_existing) {
      const { prenom, nom, telephone, email, societe, adresse, ville, code_postal, pays } = wizardData.proprietaire;
      
      // Only create contact if we have at least name
      if (nom || prenom) {
        try {
          console.log("📝 Creating new proprietaire contact...");
          
          // Prepare contact data
          const contactData = {
            type_contact: "Propriétaire",
            nom: nom || '',
            prenom: prenom || '',
            nom_societe: societe || '',
            telephones: telephone ? [{ type: "mobile", numero: telephone, principal: true }] : [],
            emails: email ? [{ email: email, type: "professionnel", principal: true }] : [],
            adresse: adresse || wizardData.proprietaire.adresse_complete || '',
            code_postal: code_postal || '',
            ville: ville || '',
            pays: pays || 'France',
            groupes: [],
            newsletter: false
          };
          
          const contactResponse = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/contacts`,
            contactData
          );
          
          console.log("✅ Contact propriétaire créé:", contactResponse.data.id);
          
          // Update wizardData with the created contact_id
          wizardData.proprietaire.contact_id = contactResponse.data.id;
          
        } catch (contactError) {
          console.error("⚠️ Erreur création contact propriétaire:", contactError);
          // Continue anyway - the property can still be created without contact link
        }
      }
    }
    
    // Step 2: Save the property draft
    console.log("💾 Calling saveDraft...");
    const savedDraft = await saveDraft();
    
    // Check if there was an error
    if (savedDraft.error) {
      console.error("❌ Draft creation failed");
      alert("Erreur lors de la création du bien. Veuillez réessayer.");
      return;
    }
    
    console.log("✅ saveDraft completed successfully, draft ID:", savedDraft.id);
    
    // Step 3: Show completion modal with options
    const action = window.confirm(
      `✅ Bien créé avec succès en brouillon!\n\n` +
      (wizardData.proprietaire?.contact_id ? `✅ Contact propriétaire ajouté\n\n` : '') +
      `Pour l'activer, ajoutez un numéro de mandat.\n\n` +
      `Que souhaitez-vous faire?\n` +
      `• OK = Voir/modifier le bien créé\n` +
      `• Annuler = Retourner au dashboard`
    );
    
    if (action && savedDraft?.id && onPropertyCreated) {
      // User wants to view/edit the created property
      onPropertyCreated(savedDraft.id);
    } else {
      // Return to dashboard
      onClose();
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Type de transaction</h2>
        <p className="text-gray-600">Sélectionnez le type de transaction pour ce bien</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
        {(wizardTypes.transaction_types || []).map((type) => (
          <button
            key={type.value}
            onClick={() => {
              setWizardData(prev => ({ ...prev, transaction_type: type.value }));
            }}
            className={`p-6 rounded-xl border-2 transition-all duration-200 text-left group hover:scale-105 ${
              wizardData.transaction_type === type.value
                ? 'border-blue-500 bg-blue-50 shadow-lg' 
                : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
            }`}
          >
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                wizardData.transaction_type === type.value 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-600'
              }`}>
                <i className={`bi ${type.value === 'Vente' ? 'bi-tag' : 'bi-house'} text-xl`}></i>
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900">{type.label}</h3>
                <p className="text-sm text-gray-500">
                  {type.value === 'Vente' ? 'Vente de fonds, murs, licences...' : 'Location de locaux, bureaux...'}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => {
    const filteredCategories = (wizardTypes.categories || []).filter(cat => {
      if (wizardData.transaction_type === 'Vente') {
        return !cat.value.includes('(Location)');
      } else {
        return cat.value.includes('(Location)') || cat.value === 'Autres locations';
      }
    });

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Catégorie de bien</h2>
          <p className="text-gray-600">Sélectionnez la catégorie principale</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {filteredCategories.map((category) => {
            // Ajouter "(en cours)" pour Investissement
            const displayLabel = category.value === 'Investissement / Produits de rapport' 
              ? 'Investissement (en cours)'
              : category.label;
            
            return (
              <button
                key={category.value}
                onClick={() => {
                  setWizardData(prev => ({ ...prev, category: category.value, subcategory: '' }));
                }}
                className={`p-4 rounded-xl border-2 transition-all duration-200 text-left hover:scale-105 ${
                  wizardData.category === category.value
                    ? 'border-blue-500 bg-blue-50 shadow-lg' 
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    wizardData.category === category.value 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <i className="bi bi-building text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 text-sm leading-tight">{displayLabel}</h3>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const subcategories = (wizardTypes.subcategories && wizardTypes.subcategories[wizardData.category]) || [];

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Type d'activité</h2>
          <p className="text-gray-600">Précisez le type d'activité</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          {subcategories.map((subcat) => (
            <button
              key={subcat.value}
              onClick={() => {
                setWizardData(prev => ({ ...prev, subcategory: subcat.value }));
              }}
              className={`p-4 rounded-xl border-2 transition-all duration-200 text-left hover:scale-105 ${
                wizardData.subcategory === subcat.value
                  ? 'border-green-500 bg-green-50 shadow-lg' 
                  : 'border-gray-200 hover:border-green-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  wizardData.subcategory === subcat.value 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <i className="bi bi-shop text-sm"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 text-sm leading-tight">{subcat.label}</h3>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    // Dynamic fields configuration based on subcategory
    const getDynamicFields = () => {
      const subcat = wizardData.subcategory;
      const category = wizardData.category;
      const isLicence = category?.includes('Licences') || category?.includes('Licence') || subcat?.toLowerCase().includes('licence');
      
      // Common fields for all
      const commonFields = {
        prix_estime: true,
        // Pas de surface pour les licences (actif incorporel)
        superficie: !isLicence,
        numero_mandat: true
      };
      
      // Additional fields based on type
      const specificFields = {};
      
      // Restaurant/Bar specific (uniquement si pas une licence pure)
      if (!isLicence && (subcat?.toLowerCase().includes('restaurant') || subcat?.toLowerCase().includes('bar') || subcat?.toLowerCase().includes('café'))) {
        specificFields.nombre_couverts = true;
        specificFields.terrasse = true;
        specificFields.cuisine_equipee = true;
      }
      
      // Licence specific
      if (isLicence) {
        specificFields.type_licence = true;
        specificFields.validite_licence = true;
        specificFields.transferable = true;
        specificFields.commune_licence = true;
      }
      
      // Commerce retail specific (pas pour licence)
      if (!isLicence && (subcat?.toLowerCase().includes('commerce') || subcat?.toLowerCase().includes('boutique'))) {
        specificFields.vitrine = true;
        specificFields.reserve = true;
        specificFields.emplacement_commercial = true;
      }
      
      // Bureau specific (pas pour licence)
      if (!isLicence && category?.includes('Bureaux')) {
        specificFields.postes_travail = true;
        specificFields.salles_reunion = true;
        specificFields.parking_inclus = true;
      }
      
      return { ...commonFields, ...specificFields };
    };
    
    const fields = getDynamicFields();
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Informations générales</h2>
          <p className="text-gray-600">Renseignez les informations de base</p>
          {wizardData.subcategory && (
            <div className="mt-2 inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
              Type: {wizardData.subcategory}
            </div>
          )}
        </div>

        <div className="max-w-2xl mx-auto space-y-4">
          {/* Common fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.prix_estime && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prix estimé (€)</label>
                <input
                  type="number"
                  value={wizardData.general_info.prix_estime || ''}
                  onChange={(e) => setWizardData(prev => ({ 
                    ...prev, 
                    general_info: { ...prev.general_info, prix_estime: parseFloat(e.target.value) }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="120000"
                />
              </div>
            )}
            {fields.superficie && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Surface (m²)</label>
                <input
                  type="number"
                  value={wizardData.general_info.superficie || ''}
                  onChange={(e) => setWizardData(prev => ({ 
                    ...prev, 
                    general_info: { ...prev.general_info, superficie: parseFloat(e.target.value) }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="85"
                />
              </div>
            )}
          </div>

          {/* Restaurant/Bar specific fields */}
          {(fields.nombre_couverts || fields.terrasse || fields.cuisine_equipee) && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Spécifique Restaurant/Bar</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.nombre_couverts && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de couverts</label>
                    <input
                      type="number"
                      value={wizardData.general_info.nombre_couverts || ''}
                      onChange={(e) => setWizardData(prev => ({ 
                        ...prev, 
                        general_info: { ...prev.general_info, nombre_couverts: parseInt(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="50"
                    />
                  </div>
                )}
                {fields.terrasse && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Terrasse</label>
                    <select
                      value={wizardData.general_info.terrasse || ''}
                      onChange={(e) => setWizardData(prev => ({ 
                        ...prev, 
                        general_info: { ...prev.general_info, terrasse: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Non spécifié</option>
                      <option value="oui">Oui</option>
                      <option value="non">Non</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Licence specific fields */}
          {(fields.type_licence || fields.validite_licence || fields.transferable) && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Informations Licence</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.type_licence && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type de licence</label>
                    <input
                      type="text"
                      value={wizardData.general_info.type_licence || ''}
                      onChange={(e) => setWizardData(prev => ({ 
                        ...prev, 
                        general_info: { ...prev.general_info, type_licence: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Type de licence..."
                    />
                  </div>
                )}
                {fields.transferable && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Transférable</label>
                    <select
                      value={wizardData.general_info.transferable || ''}
                      onChange={(e) => setWizardData(prev => ({ 
                        ...prev, 
                        general_info: { ...prev.general_info, transferable: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Non spécifié</option>
                      <option value="oui">Oui</option>
                      <option value="non">Non</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bureau specific fields */}
          {(fields.postes_travail || fields.salles_reunion || fields.parking_inclus) && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Spécifique Bureaux</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.postes_travail && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de postes</label>
                    <input
                      type="number"
                      value={wizardData.general_info.postes_travail || ''}
                      onChange={(e) => setWizardData(prev => ({ 
                        ...prev, 
                        general_info: { ...prev.general_info, postes_travail: parseInt(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="10"
                    />
                  </div>
                )}
                {fields.parking_inclus && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Parking inclus</label>
                    <select
                      value={wizardData.general_info.parking_inclus || ''}
                      onChange={(e) => setWizardData(prev => ({ 
                        ...prev, 
                        general_info: { ...prev.general_info, parking_inclus: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Non spécifié</option>
                      <option value="oui">Oui</option>
                      <option value="non">Non</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Mandat field at the end */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            {fields.numero_mandat && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Numéro de mandat (optionnel)</label>
                <input
                  type="text"
                  value={wizardData.general_info.numero_mandat || ''}
                  onChange={(e) => setWizardData(prev => ({ 
                    ...prev, 
                    general_info: { ...prev.general_info, numero_mandat: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Laisser vide pour créer un brouillon"
                />
                <p className="text-xs text-gray-500 mt-1">
                  <i className="bi bi-lightbulb-fill text-yellow-500"></i> Sans numéro de mandat, le bien sera sauvegardé en brouillon
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Localisation</h2>
        <p className="text-gray-600">Adresse et informations de localisation du bien</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        {/* Address Autocomplete */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adresse complète *
            <span className="ml-2 text-xs text-blue-600"><i className="bi bi-lightbulb-fill text-yellow-500"></i> Commencez à taper pour voir les suggestions</span>
          </label>
          <input
            type="text"
            value={wizardData.location.adresse_complete || ''}
            onChange={(e) => {
              setWizardData(prev => ({ 
                ...prev, 
                location: { ...prev.location, adresse_complete: e.target.value }
              }));
            }}
            onFocus={() => {
              if (addressSuggestions.length > 0) {
                setShowAddressSuggestions(true);
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="15 Avenue Monte-Carlo, Monaco"
            required
          />
          
          {/* Loading indicator */}
          {loadingAddresses && (
            <div className="absolute right-3 top-10">
              <i className="bi bi-arrow-repeat animate-spin text-blue-500"></i>
            </div>
          )}
          
          {/* Autocomplete dropdown */}
          {showAddressSuggestions && addressSuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              <div className="p-2 text-xs text-gray-500 bg-blue-50 border-b border-blue-100">
                <i className="bi bi-globe"></i> Adresses internationales (OpenStreetMap)
              </div>
              {addressSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    const address = suggestion.properties.label;
                    const city = suggestion.properties.city || '';
                    const postcode = suggestion.properties.postcode || '';
                    const country = suggestion.properties.country || 'France';
                    
                    console.log("✅ Adresse sélectionnée:", { address, city, postcode, country });
                    
                    setWizardData(prev => ({
                      ...prev,
                      location: {
                        ...prev.location,
                        adresse_complete: address,
                        adresse: address.split(',')[0], // Première partie avant la virgule
                        ville: city,
                        code_postal: postcode,
                        pays: country
                      }
                    }));
                    setShowAddressSuggestions(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
                >
                  <div className="flex items-start">
                    <i className="bi bi-geo-alt text-blue-500 mr-2 mt-0.5"></i>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{suggestion.properties.label}</div>
                      <div className="text-xs text-gray-600">{suggestion.properties.city} {suggestion.properties.postcode}</div>
                    </div>
                  </div>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowAddressSuggestions(false)}
                className="w-full text-center px-4 py-2 text-xs text-gray-500 hover:text-gray-700 border-t border-gray-200"
              >
                Fermer <i className="bi bi-x"></i>
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
            <input
              type="text"
              value={wizardData.location.ville || ''}
              onChange={(e) => setWizardData(prev => ({ 
                ...prev, 
                location: { ...prev.location, ville: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Monaco"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
            <input
              type="text"
              value={wizardData.location.code_postal || ''}
              onChange={(e) => setWizardData(prev => ({ 
                ...prev, 
                location: { ...prev.location, code_postal: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="98000"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Secteur/Quartier</label>
            <input
              type="text"
              value={wizardData.location.secteur || ''}
              onChange={(e) => setWizardData(prev => ({ 
                ...prev, 
                location: { ...prev.location, secteur: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Centre-ville, Port Hercule..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Étage (si applicable)</label>
            <input
              type="text"
              value={wizardData.location.etage || ''}
              onChange={(e) => setWizardData(prev => ({ 
                ...prev, 
                location: { ...prev.location, etage: e.target.value }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Rez-de-chaussée, 1er étage..."
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Informations d'accès</label>
          <textarea
            value={wizardData.location.acces_info || ''}
            onChange={(e) => setWizardData(prev => ({ 
              ...prev, 
              location: { ...prev.location, acces_info: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-20"
            placeholder="Proximité parking, métro, accès PMR..."
          />
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Propriétaire</h2>
        <p className="text-gray-600">Sélectionnez un contact existant ou créez-en un nouveau</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        {/* Contact Search / Autocomplete */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rechercher un contact existant
            <span className="ml-2 text-xs text-blue-600"><i className="bi bi-lightbulb-fill text-yellow-500"></i> Nom, prénom ou société</span>
          </label>
          <div className="relative">
            <i className="bi bi-search absolute left-3 top-3 text-gray-400"></i>
            <input
              type="text"
              value={wizardData.proprietaire?.search_query || ''}
              onChange={(e) => {
                setWizardData(prev => ({ 
                  ...prev, 
                  proprietaire: { ...prev.proprietaire, search_query: e.target.value }
                }));
              }}
              onFocus={() => {
                if (contactSuggestions.length > 0) {
                  setShowContactSuggestions(true);
                }
              }}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Dupont, Jean, SAS Monaco..."
            />
            {loadingContacts && (
              <div className="absolute right-3 top-3">
                <i className="bi bi-arrow-repeat animate-spin text-blue-500"></i>
              </div>
            )}
          </div>
          
          {/* Contact suggestions dropdown */}
          {showContactSuggestions && contactSuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              <div className="p-2 text-xs text-gray-500 bg-green-50 border-b border-green-100">
                <i className="bi bi-people"></i> Contacts existants
              </div>
              {contactSuggestions.map((contact) => {
                const displayName = `${contact.prenom || ''} ${contact.nom || ''}`.trim() || 
                                  contact.societe || 
                                  contact.nom_societe || 
                                  'Contact sans nom';
                const telephone = contact.telephones?.[0]?.numero || '';
                const email = contact.emails?.[0]?.email || '';
                const initials = `${contact.prenom?.[0] || ''}${contact.nom?.[0] || ''}`.toUpperCase();
                
                return (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => {
                      setWizardData(prev => ({
                        ...prev,
                        proprietaire: {
                          ...prev.proprietaire,
                          contact_id: contact.id,
                          prenom: contact.prenom || '',
                          nom: contact.nom || '',
                          societe: contact.societe || contact.nom_societe || '',
                          telephone: telephone,
                          email: email,
                          search_query: displayName,
                          from_existing: true
                        }
                      }));
                      setShowContactSuggestions(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-green-50 border-b border-gray-100 last:border-0 transition-colors"
                  >
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-semibold mr-3">
                        {initials || '??'}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{displayName}</div>
                        {telephone && (
                          <div className="text-xs text-gray-600">
                            <i className="bi bi-telephone mr-1"></i>{telephone}
                          </div>
                        )}
                        {email && (
                          <div className="text-xs text-gray-600">
                            <i className="bi bi-envelope mr-1"></i>{email}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setShowContactSuggestions(false)}
                className="w-full text-center px-4 py-2 text-xs text-gray-500 hover:text-gray-700 border-t border-gray-200"
              >
                Fermer <i className="bi bi-x"></i>
              </button>
            </div>
          )}
        </div>
        
        {/* Separator */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">ou créer un nouveau contact</span>
          </div>
        </div>

        {/* Manual entry fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
            <input
              type="text"
              value={wizardData.proprietaire?.prenom || ''}
              onChange={(e) => setWizardData(prev => ({ 
                ...prev, 
                proprietaire: { ...prev.proprietaire, prenom: e.target.value, from_existing: false }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Jean"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
            <input
              type="text"
              value={wizardData.proprietaire?.nom || ''}
              onChange={(e) => setWizardData(prev => ({ 
                ...prev, 
                proprietaire: { ...prev.proprietaire, nom: e.target.value, from_existing: false }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Dupont"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
            <input
              type="tel"
              value={wizardData.proprietaire?.telephone || ''}
              onChange={(e) => setWizardData(prev => ({ 
                ...prev, 
                proprietaire: { ...prev.proprietaire, telephone: e.target.value, from_existing: false }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="+33 6 12 34 56 78"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={wizardData.proprietaire?.email || ''}
              onChange={(e) => setWizardData(prev => ({ 
                ...prev, 
                proprietaire: { ...prev.proprietaire, email: e.target.value, from_existing: false }
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="jean.dupont@email.com"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Société/Organisation (optionnel)</label>
          <input
            type="text"
            value={wizardData.proprietaire?.societe || ''}
            onChange={(e) => setWizardData(prev => ({ 
              ...prev, 
              proprietaire: { ...prev.proprietaire, societe: e.target.value, from_existing: false }
            }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="SAS Monaco Immobilier"
          />
        </div>

        {/* Address fields - Only show if creating new contact */}
        {!wizardData.proprietaire?.from_existing && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Adresse du propriétaire (optionnel)</h3>
            
            {/* Address autocomplete */}
            <div className="relative mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse complète
                <span className="ml-2 text-xs text-blue-600"><i className="bi bi-lightbulb-fill text-yellow-500"></i> International - tous pays</span>
              </label>
              <input
                type="text"
                value={wizardData.proprietaire?.adresse_complete || ''}
                onChange={(e) => {
                  setWizardData(prev => ({ 
                    ...prev, 
                    proprietaire: { ...prev.proprietaire, adresse_complete: e.target.value }
                  }));
                }}
                onFocus={() => {
                  if (ownerAddressSuggestions.length > 0) {
                    setShowOwnerAddressSuggestions(true);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Via Roma 123, Milano, Italy"
              />
              
              {loadingOwnerAddresses && (
                <div className="absolute right-3 top-10">
                  <i className="bi bi-arrow-repeat animate-spin text-blue-500"></i>
                </div>
              )}
              
              {/* Owner address suggestions dropdown */}
              {showOwnerAddressSuggestions && ownerAddressSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  <div className="p-2 text-xs text-gray-500 bg-slate-50 border-b border-slate-100">
                    <i className="bi bi-globe"></i> Adresses internationales
                  </div>
                  {ownerAddressSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        const address = suggestion.properties.label;
                        const city = suggestion.properties.city;
                        const postcode = suggestion.properties.postcode;
                        const country = suggestion.properties.country;
                        
                        setWizardData(prev => ({
                          ...prev,
                          proprietaire: {
                            ...prev.proprietaire,
                            adresse_complete: address,
                            adresse: suggestion.properties.street || address.split(',')[0],
                            ville: city,
                            code_postal: postcode,
                            pays: country
                          }
                        }));
                        setShowOwnerAddressSuggestions(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
                    >
                      <div className="flex items-start">
                        <i className="bi bi-geo-alt text-blue-500 mr-2 mt-0.5"></i>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{suggestion.properties.label}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setShowOwnerAddressSuggestions(false)}
                    className="w-full text-center px-4 py-2 text-xs text-gray-500 hover:text-gray-700 border-t border-gray-200"
                  >
                    Fermer <i className="bi bi-x"></i>
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                <input
                  type="text"
                  value={wizardData.proprietaire?.ville || ''}
                  onChange={(e) => setWizardData(prev => ({ 
                    ...prev, 
                    proprietaire: { ...prev.proprietaire, ville: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Monaco"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
                <input
                  type="text"
                  value={wizardData.proprietaire?.code_postal || ''}
                  onChange={(e) => setWizardData(prev => ({ 
                    ...prev, 
                    proprietaire: { ...prev.proprietaire, code_postal: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="98000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
                <input
                  type="text"
                  value={wizardData.proprietaire?.pays || ''}
                  onChange={(e) => setWizardData(prev => ({ 
                    ...prev, 
                    proprietaire: { ...prev.proprietaire, pays: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Monaco"
                />
              </div>
            </div>
          </div>
        )}

        {/* Négociateur field */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Négociateur
            <span className="ml-2 text-xs text-gray-500">(optionnel)</span>
          </label>
          <select
            value={wizardData.negociateur || ''}
            onChange={(e) => setWizardData(prev => ({ 
              ...prev, 
              negociateur: e.target.value
            }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Sélectionner un négociateur...</option>
            {agencyNegotiators.map(nego => (
              <option key={nego.id} value={nego.id}>
                {nego.prenom} {nego.nom} ({nego.role === 'admin_agence' ? 'Admin' : 'Agent'})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Notes/Remarques</label>
          <textarea
            value={wizardData.proprietaire?.notes || ''}
            onChange={(e) => setWizardData(prev => ({ 
              ...prev, 
              proprietaire: { ...prev.proprietaire, notes: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-20"
            placeholder="Informations particulières, préférences de contact..."
          />
        </div>
        
        {/* Info badge if contact selected */}
        {wizardData.proprietaire?.from_existing && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
            <i className="bi bi-check-circle text-green-600 mr-2"></i>
            <span className="text-sm text-green-800">Contact existant sélectionné</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Descriptifs</h2>
        <p className="text-gray-600">Description du bien</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description principale</label>
          <textarea
            value={wizardData.descriptions.description || ''}
            onChange={(e) => setWizardData(prev => ({ 
              ...prev, 
              descriptions: { ...prev.descriptions, description: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24"
            placeholder="Description du bien..."
          />
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch(wizardStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      default: return renderStep1();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Création de bien - Étape par étape</h1>
            <button
              onClick={() => onClose && onClose()}
              className="text-gray-500 hover:text-gray-700"
            >
              <i className="bi bi-x-lg text-xl"></i>
            </button>
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Étape {wizardStep} sur 7</span>
              <span className="text-sm text-gray-500">{getStepTitle()}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(wizardStep / 7) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Steps indicator */}
          <div className="flex items-center justify-center space-x-4 mb-6">
            {[1,2,3,4,5,6,7].map((step) => (
              <div key={step} className={`flex items-center ${step < 6 ? 'flex-1' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                  step === wizardStep 
                    ? 'bg-blue-600 text-white shadow-lg' 
                    : step < wizardStep 
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step < wizardStep ? <i className="bi bi-check"></i> : step}
                </div>
                {step < 7 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    step < wizardStep ? 'bg-green-500' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          {renderCurrentStep()}
        </div>

        {/* Navigation buttons */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={wizardStep === 1}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                wizardStep === 1 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <i className="bi bi-chevron-left"></i>
              <span>Précédent</span>
            </button>

            <button
              onClick={wizardStep === 7 ? finishWizard : nextStep}
              disabled={
                (wizardStep === 1 && !wizardData.transaction_type) ||
                (wizardStep === 2 && !wizardData.category) ||
                (wizardStep === 3 && !wizardData.subcategory)
              }
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <span>{wizardStep === 7 ? 'Terminer' : 'Suivant'}</span>
              <i className={`bi ${wizardStep === 7 ? 'bi-check-lg' : 'bi-chevron-right'}`}></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyWizard;
